export interface StagedReferenceAlignmentStageReport {
  stage: string
  status: string
  evaluation_mode: string
  output_dir?: string
  summary: Record<string, unknown>
}

export interface StagedReferenceAlignmentErrorDetail {
  message?: string
  kind?: string
  [key: string]: unknown
}

export interface StagedReferenceAlignmentClusterReference {
  cluster_id: string
  cluster_size: number
  ground_truth: string
  ordered_members: string[]
  selected_members: string[]
}

export interface StagedReferenceAlignmentClusterIntake {
  analysis_witness_sigla: string[]
  selected_witness_sigla: string[]
  clusters: StagedReferenceAlignmentClusterReference[]
  recommended_ground_truth?: string | null
  recommendation_reason?: string | null
}

export class StagedReferenceAlignmentApiError extends Error {
  status?: number
  detail?: string | StagedReferenceAlignmentErrorDetail

  constructor(message: string, options?: { status?: number; detail?: string | StagedReferenceAlignmentErrorDetail }) {
    super(message)
    this.name = 'StagedReferenceAlignmentApiError'
    this.status = options?.status
    this.detail = options?.detail
  }
}

export interface StagedReferenceAlignmentArtifact {
  format_version: string
  dataset_name: 'rd_p8v3' | 'generic_context'
  mode: 'replay' | 'live'
  project_id?: string
  work_id?: string
  context_id?: string
  created_at: string
  summary: {
    row_count?: number
    witness_count?: number
    scene_count?: number | null
    [key: string]: unknown
  }
  diagnostics: Record<string, unknown>
  chapter_import_payload: Record<string, unknown>
}

export interface StagedReferenceAlignmentRunResponse {
  run_id: string
  status: 'completed'
  artifact: StagedReferenceAlignmentArtifact
  stage_reports: Record<string, StagedReferenceAlignmentStageReport>
}

export interface StagedReferenceAlignmentWitnessReplayResponse {
  run_id: string
  status: 'completed'
  source_run_dir: string
  matched_witness_sigla: string[]
  artifact: StagedReferenceAlignmentArtifact
}

export interface StagedReferenceAlignmentSavedRunInputWitness {
  witness_id: string
  sigla: string
  label?: string | null
  source_file_name?: string | null
}

export interface StagedReferenceAlignmentSavedRun {
  output_root: string
  dataset_name: 'rd_p8v3' | 'generic_context' | string
  project_id?: string | null
  work_id?: string | null
  context_id?: string | null
  created_at: string
  updated_at?: string | null
  status: string
  current_stage: string
  next_build_scope?: 'scene_backbone' | 'cluster_realization' | 'scene_joint_ledger' | 'large_n_incremental' | 'gated_witness_enrollment' | null
  resumable: boolean
  selected_witness_sigla: string[]
  representative_witness_sigla: string[]
  adaptive_build_plan?: AdaptiveBuildPlanResponse | null
  cluster_intake?: StagedReferenceAlignmentClusterIntake | null
  input_witnesses: StagedReferenceAlignmentSavedRunInputWitness[]
  trace_path?: string | null
  last_error?: Record<string, unknown> | null
  summary: Record<string, unknown>
  stage_reports: Record<string, StagedReferenceAlignmentStageReport>
}

export interface StagedReferenceAlignmentSavedRunsResponse {
  runs: StagedReferenceAlignmentSavedRun[]
}

export interface StagedReferenceAlignmentRunRequest {
  datasetName: 'rd_p8v3' | 'generic_context'
  mode: 'replay' | 'live'
  buildScope?: 'full_ledger' | 'scene_backbone' | 'cluster_realization' | 'scene_joint_ledger' | 'large_n_incremental' | 'gated_witness_enrollment'
  projectId?: string
  workId?: string
  contextId?: string
  selectedWitnessSigla?: string[]
  adaptiveBuildPlan?: AdaptiveBuildPlanResponse | null
  inputWitnesses?: Array<{
    witnessId: string
    sigla: string
    label?: string
    sourceFileName?: string
    rawSourceText: string
    cleanSourceText?: string
    sourceCleaningPolicyId?: string
    sourceCleaningReport?: unknown
    comparisonText: string
  }>
  clusterIntake?: StagedReferenceAlignmentClusterIntake
  workDisplay?: string
  contextDisplay?: string
  contextLabelPrefix?: string
  outputRoot?: string
}

export interface StagedReferenceAlignmentWitnessReplayRequest {
  projectId?: string
  workId?: string
  contextId: string
  inputWitnesses: Array<{
    witnessId: string
    sigla: string
    label?: string
    sourceFileName?: string
    rawSourceText: string
  }>
  workDisplay?: string
  contextDisplay?: string
  contextLabelPrefix?: string
}

export interface StagedReferenceAlignmentSceneMetadata {
  sceneId?: string
  sceneNumber?: number
  sceneOrdinal?: number
  title?: string
  description?: string
  arabicTitle?: string
  rowStart: number
  rowEnd: number
  rowCount?: number
  source?: string
  [key: string]: unknown
}

export interface StagedReferenceAlignmentSceneMetadataRequest {
  contextId: string
  sourceRunDir?: string | null
  expectedRowCount?: number
}

export interface StagedReferenceAlignmentSceneMetadataResponse {
  source_run_dir: string
  summary_path: string
  summary_hash: string
  scene_count: number
  row_count: number
  scenes: StagedReferenceAlignmentSceneMetadata[]
}

export interface StagedReferenceAlignmentSavedRunsRequest {
  projectId?: string
  workId?: string
  contextId: string
}

export interface StagedReferenceAlignmentSavedRunLoadRequest {
  outputRoot: string
  projectId?: string
  workId?: string
  contextId: string
  inputWitnesses?: Array<{
    witnessId: string
    sigla: string
    label?: string
    sourceFileName?: string
    rawSourceText: string
    cleanSourceText?: string
    sourceCleaningPolicyId?: string
    sourceCleaningReport?: unknown
    comparisonText: string
  }>
  workDisplay?: string
  contextDisplay?: string
  contextLabelPrefix?: string
}

export interface AdaptiveBuildPlanWitnessRequest {
  witnessId?: string
  sigla?: string
  label?: string
  sourceFileName?: string
  rawSourceText?: string
  cleanSourceText?: string
  comparisonText?: string
  language?: string | null
  processingStatus?: string | null
  witnessType?: string | null
  transcriptionType?: string | null
  coverageStatus?: string | null
  lacunoseStatus?: string | null
}

export interface AdaptiveBuildPlanRequest {
  contextId?: string
  witnesses: AdaptiveBuildPlanWitnessRequest[]
  existingRowCount?: number
  savedRunCount?: number
  clusterHint?: Record<string, string>
}

export interface AdaptiveBuildWitnessClassification {
  witness_id: string
  sigla: string
  source_sigla?: string | null
  label: string
  status: 'included' | 'partial' | 'excluded' | string
  roles: string[]
  reasons: string[]
  token_count: number
  char_count: number
  language?: string | null
  source_available: boolean
  comparison_available: boolean
  comparison_derived_from_source: boolean
  processing_status?: string | null
  has_preprocessing: boolean
  has_segmentation: boolean
  representative_score: number
  cluster_id?: string | null
}

export interface AdaptiveBuildPlanResponse {
  plan_version: string
  context_id?: string | null
  strategy: string
  strategy_rationale: string[]
  requested_witness_count: number
  eligible_witness_count: number
  included_witness_sigla: string[]
  representative_witness_sigla: string[]
  partial_witness_sigla: string[]
  outlier_witness_sigla: string[]
  translation_witness_sigla: string[]
  excluded_witness_sigla: string[]
  macro_policy: Record<string, unknown>
  micro_policy: Record<string, unknown>
  execution_phases: string[]
  review_gates: string[]
  validation_gates: string[]
  failure_policy: Record<string, unknown>
  artifact_reuse_policy: string[]
  model_policy: Record<string, unknown>
  witness_classifications: AdaptiveBuildWitnessClassification[]
}

export const requestStagedReferenceAlignmentRun = async (
  request: StagedReferenceAlignmentRunRequest
): Promise<StagedReferenceAlignmentRunResponse> => {
  const response = await fetch('/api/workbench/staged-reference-alignment/run', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      dataset_name: request.datasetName,
      mode: request.mode,
      build_scope: request.buildScope,
      project_id: request.projectId,
      work_id: request.workId,
      context_id: request.contextId,
      selected_witness_sigla: request.selectedWitnessSigla,
      adaptive_build_plan: request.adaptiveBuildPlan,
      cluster_intake: request.clusterIntake,
      input_witnesses: request.inputWitnesses?.map(witness => ({
        witness_id: witness.witnessId,
        sigla: witness.sigla,
        label: witness.label,
        source_file_name: witness.sourceFileName,
        raw_source_text: witness.rawSourceText,
        clean_source_text: witness.cleanSourceText,
        source_cleaning_policy_id: witness.sourceCleaningPolicyId,
        source_cleaning_report: witness.sourceCleaningReport,
        comparison_text: witness.comparisonText
      })),
      work_display: request.workDisplay,
      context_display: request.contextDisplay,
      context_label_prefix: request.contextLabelPrefix,
      output_root: request.outputRoot
    })
  })

  if (!response.ok) {
    const errorPayload = await response.json().catch(async () => {
      const text = await response.text().catch(() => '')
      return { text }
    })
    const fallbackStatus = [
      response.status ? `Build route returned ${response.status}` : 'Build route failed',
      response.statusText || null
    ]
      .filter(Boolean)
      .join(' ')
    const message =
      typeof errorPayload?.detail === 'string' && errorPayload.detail.trim().length > 0
        ? errorPayload.detail
        : errorPayload?.detail && typeof errorPayload.detail === 'object' && typeof errorPayload.detail.message === 'string'
          ? errorPayload.detail.message
          : typeof errorPayload?.text === 'string' && errorPayload.text.trim().length > 0
            ? fallbackStatus
            : fallbackStatus || 'Failed to build collation draft.'
    throw new StagedReferenceAlignmentApiError(message, {
      status: response.status,
      detail: errorPayload?.detail
    })
  }

  return response.json()
}

export const requestAdaptiveBuildPlan = async (
  request: AdaptiveBuildPlanRequest
): Promise<AdaptiveBuildPlanResponse> => {
  const response = await fetch('/api/workbench/staged-reference-alignment/adaptive-plan', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      context_id: request.contextId,
      witnesses: request.witnesses.map(witness => ({
        witness_id: witness.witnessId,
        sigla: witness.sigla,
        label: witness.label,
        source_file_name: witness.sourceFileName,
        raw_source_text: witness.rawSourceText,
        clean_source_text: witness.cleanSourceText,
        comparison_text: witness.comparisonText,
        language: witness.language,
        processing_status: witness.processingStatus,
        witness_type: witness.witnessType,
        transcription_type: witness.transcriptionType,
        coverage_status: witness.coverageStatus,
        lacunose_status: witness.lacunoseStatus
      })),
      existing_row_count: request.existingRowCount,
      saved_run_count: request.savedRunCount,
      cluster_hint: request.clusterHint
    })
  })

  if (!response.ok) {
    const errorPayload = await response.json().catch(async () => {
      const text = await response.text().catch(() => '')
      return { text }
    })
    const fallbackStatus = [
      response.status ? `Adaptive Build plan route returned ${response.status}` : 'Adaptive Build plan route failed',
      response.statusText || null
    ]
      .filter(Boolean)
      .join(' ')
    const message =
      typeof errorPayload?.detail === 'string' && errorPayload.detail.trim().length > 0
        ? errorPayload.detail
        : errorPayload?.detail && typeof errorPayload.detail === 'object' && typeof errorPayload.detail.message === 'string'
          ? errorPayload.detail.message
          : typeof errorPayload?.text === 'string' && errorPayload.text.trim().length > 0
            ? fallbackStatus
            : fallbackStatus || 'Failed to plan adaptive Build workflow.'
    throw new StagedReferenceAlignmentApiError(message, {
      status: response.status,
      detail: errorPayload?.detail
    })
  }

  return response.json()
}

export const requestStagedReferenceAlignmentWitnessReplay = async (
  request: StagedReferenceAlignmentWitnessReplayRequest
): Promise<StagedReferenceAlignmentWitnessReplayResponse> => {
  const response = await fetch('/api/workbench/staged-reference-alignment/replay-witnesses', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      project_id: request.projectId,
      work_id: request.workId,
      context_id: request.contextId,
      input_witnesses: request.inputWitnesses.map(witness => ({
        witness_id: witness.witnessId,
        sigla: witness.sigla,
        label: witness.label,
        source_file_name: witness.sourceFileName,
        raw_source_text: witness.rawSourceText
      })),
      work_display: request.workDisplay,
      context_display: request.contextDisplay,
      context_label_prefix: request.contextLabelPrefix
    })
  })

  if (!response.ok) {
    const errorPayload = await response.json().catch(async () => {
      const text = await response.text().catch(() => '')
      return { text }
    })
    const fallbackStatus = [
      response.status ? `Replay route returned ${response.status}` : 'Replay route failed',
      response.statusText || null
    ]
      .filter(Boolean)
      .join(' ')
    const message =
      typeof errorPayload?.detail === 'string' && errorPayload.detail.trim().length > 0
        ? errorPayload.detail
        : errorPayload?.detail && typeof errorPayload.detail === 'object' && typeof errorPayload.detail.message === 'string'
          ? errorPayload.detail.message
          : typeof errorPayload?.text === 'string' && errorPayload.text.trim().length > 0
            ? fallbackStatus
            : fallbackStatus || 'Failed to replay staged witness output.'
    throw new StagedReferenceAlignmentApiError(message, {
      status: response.status,
      detail: errorPayload?.detail
    })
  }

  return response.json()
}

export const requestStagedReferenceAlignmentSceneMetadata = async (
  request: StagedReferenceAlignmentSceneMetadataRequest
): Promise<StagedReferenceAlignmentSceneMetadataResponse> => {
  const response = await fetch('/api/workbench/staged-reference-alignment/scene-metadata', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      context_id: request.contextId,
      source_run_dir: request.sourceRunDir || undefined,
      expected_row_count: request.expectedRowCount
    })
  })

  if (!response.ok) {
    const errorPayload = await response.json().catch(async () => {
      const text = await response.text().catch(() => '')
      return { text }
    })
    const fallbackStatus = [
      response.status ? `Scene metadata route returned ${response.status}` : 'Scene metadata route failed',
      response.statusText || null
    ]
      .filter(Boolean)
      .join(' ')
    const message =
      typeof errorPayload?.detail === 'string' && errorPayload.detail.trim().length > 0
        ? errorPayload.detail
        : errorPayload?.detail && typeof errorPayload.detail === 'object' && typeof errorPayload.detail.message === 'string'
          ? errorPayload.detail.message
          : typeof errorPayload?.text === 'string' && errorPayload.text.trim().length > 0
            ? fallbackStatus
            : fallbackStatus || 'Failed to load scene metadata.'
    throw new StagedReferenceAlignmentApiError(message, {
      status: response.status,
      detail: errorPayload?.detail
    })
  }

  return response.json()
}

export const requestStagedReferenceAlignmentSavedRuns = async (
  request: StagedReferenceAlignmentSavedRunsRequest
): Promise<StagedReferenceAlignmentSavedRunsResponse> => {
  const search = new URLSearchParams()
  if (request.projectId) {
    search.set('project_id', request.projectId)
  }
  if (request.workId) {
    search.set('work_id', request.workId)
  }
  search.set('context_id', request.contextId)

  const response = await fetch(`/api/workbench/staged-reference-alignment/runs?${search.toString()}`)
  if (!response.ok) {
    const errorPayload = await response.json().catch(async () => {
      const text = await response.text().catch(() => '')
      return { text }
    })
    const fallbackStatus = [
      response.status ? `Runs route returned ${response.status}` : 'Runs route failed',
      response.statusText || null
    ]
      .filter(Boolean)
      .join(' ')
    const message =
      typeof errorPayload?.detail === 'string' && errorPayload.detail.trim().length > 0
        ? errorPayload.detail
        : errorPayload?.detail && typeof errorPayload.detail === 'object' && typeof errorPayload.detail.message === 'string'
          ? errorPayload.detail.message
          : typeof errorPayload?.text === 'string' && errorPayload.text.trim().length > 0
            ? fallbackStatus
            : fallbackStatus || 'Failed to list staged runs.'
    throw new StagedReferenceAlignmentApiError(message, {
      status: response.status,
      detail: errorPayload?.detail
    })
  }

  return response.json()
}

export const requestStagedReferenceAlignmentSavedRun = async (
  request: StagedReferenceAlignmentSavedRunLoadRequest
): Promise<StagedReferenceAlignmentRunResponse> => {
  const response = await fetch('/api/workbench/staged-reference-alignment/load-run', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      output_root: request.outputRoot,
      project_id: request.projectId,
      work_id: request.workId,
      context_id: request.contextId,
      input_witnesses: request.inputWitnesses?.map(witness => ({
        witness_id: witness.witnessId,
        sigla: witness.sigla,
        label: witness.label,
        source_file_name: witness.sourceFileName,
        raw_source_text: witness.rawSourceText,
        clean_source_text: witness.cleanSourceText,
        source_cleaning_policy_id: witness.sourceCleaningPolicyId,
        source_cleaning_report: witness.sourceCleaningReport,
        comparison_text: witness.comparisonText,
      })),
      work_display: request.workDisplay,
      context_display: request.contextDisplay,
      context_label_prefix: request.contextLabelPrefix,
    })
  })

  if (!response.ok) {
    const errorPayload = await response.json().catch(async () => {
      const text = await response.text().catch(() => '')
      return { text }
    })
    const fallbackStatus = [
      response.status ? `Saved run route returned ${response.status}` : 'Saved run route failed',
      response.statusText || null
    ]
      .filter(Boolean)
      .join(' ')
    const message =
      typeof errorPayload?.detail === 'string' && errorPayload.detail.trim().length > 0
        ? errorPayload.detail
        : errorPayload?.detail && typeof errorPayload.detail === 'object' && typeof errorPayload.detail.message === 'string'
          ? errorPayload.detail.message
          : typeof errorPayload?.text === 'string' && errorPayload.text.trim().length > 0
            ? fallbackStatus
            : fallbackStatus || 'Failed to load saved staged run.'
    throw new StagedReferenceAlignmentApiError(message, {
      status: response.status,
      detail: errorPayload?.detail
    })
  }

  return response.json()
}
